<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import LaisSelect from 'dashboard/components-next/LaisSelect.vue';

const props = defineProps({
  page: { type: Number, required: true },
  pageSize: { type: Number, required: true },
  total: { type: Number, required: true },
});

const emit = defineEmits(['changePage', 'changePageSize']);
const { t } = useI18n();
const totalPages = computed(() =>
  Math.max(1, Math.ceil(props.total / props.pageSize))
);
const pageSizes = computed(() =>
  [
    ...new Set([
      props.pageSize,
      ...[5, 15, 30].filter(size => props.total / size > 0.5),
    ]),
  ]
    .sort((first, second) => first - second)
    .map(size => ({ label: String(size), value: size }))
);
const range = computed(() => {
  if (!props.total) return { from: 0, to: 0 };
  const from = (props.page - 1) * props.pageSize + 1;
  return { from, to: Math.min(props.total, from + props.pageSize - 1) };
});
</script>

<template>
  <div
    class="flex flex-wrap items-center justify-end gap-2 text-sm text-lais-text-muted"
  >
    <label class="flex items-center gap-2">
      <span>{{ t('POST_SALE.MANAGEMENT.PAGINATION.PER_PAGE') }}</span>
      <LaisSelect
        :model-value="pageSize"
        :options="pageSizes"
        :placeholder="t('POST_SALE.MANAGEMENT.SELECT')"
        @update:model-value="emit('changePageSize', Number($event))"
      />
    </label>
    <span v-if="total > pageSize">
      {{
        t('POST_SALE.MANAGEMENT.PAGINATION.RANGE', {
          from: range.from,
          to: range.to,
          count: total,
        })
      }}
    </span>
    <span v-else>{{
      t('POST_SALE.MANAGEMENT.PAGINATION.TOTAL', { count: total })
    }}</span>
    <div v-if="totalPages > 1" class="flex items-center gap-1">
      <LaisPrimaryButton
        variant="ghost"
        :disabled="page === 1"
        @click="emit('changePage', page - 1)"
      >
        {{ t('POST_SALE.MANAGEMENT.PAGINATION.PREVIOUS') }}
      </LaisPrimaryButton>
      <button
        v-for="item in totalPages"
        :key="item"
        type="button"
        class="grid size-8 place-items-center rounded-full text-sm font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-lais-primary-50"
        :class="
          item === page
            ? 'bg-lais-primary text-white'
            : 'hover:bg-lais-primary-50 text-lais-text-body'
        "
        :aria-current="item === page ? 'page' : undefined"
        @click="emit('changePage', item)"
      >
        {{ item }}
      </button>
      <LaisPrimaryButton
        variant="ghost"
        :disabled="page === totalPages"
        @click="emit('changePage', page + 1)"
      >
        {{ t('POST_SALE.MANAGEMENT.PAGINATION.NEXT') }}
      </LaisPrimaryButton>
    </div>
  </div>
</template>
